import { PackageMinus, PackagePlus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useInventoryStore } from '../store/inventoryStore'
import { useDownloadInvoiceMutation } from '../hooks'
import { inventoryReasonLabels } from '../constants/inventory'
import type { InventoryReason } from '../constants/inventory'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { Modal } from '@/components/custom/modal'
import { formatCurrency } from '@/utils/currencyUtils'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/utils/timeZoneUtils'
import { useUsersStore } from '@/features/users/store/usersStore'
import { Button } from '@/components/ui/button'
import { useMembersStore } from '@/features/members/store/membersStore'

interface TransactionDetailsProps {
  trigger?: React.ReactNode
  open: boolean
  onClose: () => void
  transactionId?: string | null
}

export function TransactionDetails({
  trigger,
  open,
  onClose,
  transactionId,
}: TransactionDetailsProps) {
  const [viewInvoice, setViewInvoice] = useState(false)
  const [invoiceUrl, setInvoiceUrl] = useState<string>('')

  const { mutateAsync: downloadInvoiceMutation, isPending } =
    useDownloadInvoiceMutation()

  const transactions = useInventoryStore((state) => state.transactions)
  const inventoryStock = useInventoryStore((state) => state.inventoryStock)
  const categories = useInventoryStore((state) => state.categories)
  const churches = useChurchesStore((state) => state.churches)
  const users = useUsersStore((state) => state.users)
  const members = useMembersStore((state) => state.members)

  const transaction = useMemo(
    () => transactions.find((t) => t._id === transactionId),
    [transactions, transactionId],
  )
  const item = useMemo(
    () => inventoryStock.find((i) => i.item === transaction?.item),
    [inventoryStock, transaction],
  )
  const category = useMemo(
    () => categories.find((c) => c._id === item?.categoryId),
    [categories, item],
  )
  const church = useMemo(
    () => churches.find((c) => c._id === transaction?.churchId),
    [churches, transaction],
  )
  const createdByUser = useMemo(
    () => users.find((u) => u._id === transaction?.createdBy),
    [users, transaction],
  )
  const authorizedByMember = useMemo(
    () => members.find((m) => m._id === transaction?.disposal?.authorizedBy),
    [members, transaction],
  )

  const handleViewInvoice = async () => {
    if (!transactionId) return

    const response = await downloadInvoiceMutation(transactionId)
    if (response.success && response.downloadUrl) {
      setInvoiceUrl(response.downloadUrl)
      setViewInvoice(true)
    }
  }

  if (!transaction) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        trigger={trigger}
        title="Transaction Details"
        direction="right"
        contentClassName="min-w-[98vw]"
      >
        <div className="text-center py-8 text-gray-500">
          Transaction not found
        </div>
      </Modal>
    )
  }

  const isInTransaction = transaction.type === 'IN'
  const transactionDate = transaction.createdAt
    ? formatDate(transaction.createdAt, 'DD MMMM YYYY')
    : 'N/A'

  const getDisposalReasonLabel = (reason: InventoryReason) => {
    return inventoryReasonLabels[reason]
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      trigger={trigger}
      title="Transaction Details"
      direction="right"
      contentClassName="min-w-[98vw]"
    >
      <div className="space-y-6">
        {/* Transaction Type Badge */}
        <div className="flex justify-between items-center gap-3">
          <h2 className="text-gray-500">{transactionDate}</h2>
          {transaction.type === 'IN' ? (
            <Badge
              variant="outline"
              className="text-sm inline-flex items-center gap-2 text-green-600 border-green-300 bg-green-100"
            >
              <PackagePlus className="h-3 w-3" />
              <span>In</span>
            </Badge>
          ) : (
            <Badge
              variant="default"
              className="text-sm inline-flex items-center gap-2 text-red-600 border-red-300 bg-red-100"
            >
              <PackageMinus className="h-3 w-3" />
              <span>Out</span>
            </Badge>
          )}
        </div>

        {/* Basic Information */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">
                Church
              </label>
              <p className="mt-1 text-base">{church?.name || 'N/A'}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600">
                Category
              </label>
              <p className="mt-1 text-base">{category?.name || 'N/A'}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                Item
              </label>
              <p className="mt-1 text-base font-medium">
                {item?.item || 'N/A'} {item?.brand && `(${item.brand})`}
              </p>
              {item?.description && (
                <p className="text-sm text-gray-500">{item.description}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600">
                Quantity
              </label>
              <p className="mt-1 text-base font-semibold">
                {transaction.quantity} {item?.unit || ''}
              </p>
            </div>
          </div>
        </div>

        {/* Acquisition Details (for IN transactions) */}
        {isInTransaction && transaction.acquisition && (
          <div className="border-t pt-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">
              Acquisition Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Price
                </label>
                <p className="mt-1 text-base font-semibold text-green-600">
                  {formatCurrency(transaction.acquisition.price || 0)}
                </p>
              </div>

              {transaction.acquisition.purchaseDate && (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Purchase Date
                  </label>
                  <p className="mt-1 text-base">
                    {formatDate(
                      transaction.acquisition.purchaseDate,
                      'DD MMMM YYYY',
                    )}
                  </p>
                </div>
              )}

              {transaction.acquisition.invoiceNumber && (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Invoice Number
                  </label>
                  <p className="mt-1 text-base">
                    {transaction.acquisition.invoiceNumber}
                  </p>
                </div>
              )}

              {transaction.acquisition.provider && (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Provider / Supplier
                  </label>
                  <p className="mt-1 text-base">
                    {transaction.acquisition.provider}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Disposal Details (for OUT transactions) */}
        {!isInTransaction && transaction.disposal && (
          <div className="border-t pt-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">
              Disposal Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Reason
                </label>
                <p className="mt-1 text-base">
                  {getDisposalReasonLabel(transaction.disposal.reason)}
                </p>
              </div>

              {authorizedByMember && (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Authorized By
                  </label>
                  <p className="mt-1 text-base">{authorizedByMember.name}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {transaction.notes && (
          <div className="border-t pt-4">
            <label className="text-sm font-medium text-gray-600">Notes</label>
            <p className="mt-1 text-base whitespace-pre-wrap">
              {transaction.notes}
            </p>
          </div>
        )}

        {/* Created By */}
        <div className="flex flex-col md:flex-row justify-between border-t pt-4 text-sm text-gray-500 gap-3">
          {transaction.acquisition?.invoiceUrl && (
            <Button
              onClick={handleViewInvoice}
              disabled={isPending}
              className="w-full md:w-auto"
            >
              {isPending ? 'Loading...' : 'View Invoice'}
            </Button>
          )}
          {transaction.createdBy && (
            <p className="justify-end">
              Created by: {createdByUser?.name || transaction.createdBy}
            </p>
          )}
        </div>

        {/* Modals for actions */}
        {viewInvoice && invoiceUrl && (
          <Modal
            open={viewInvoice}
            onClose={() => {
              setViewInvoice(false)
              setInvoiceUrl('')
            }}
            title="Invoice"
            contentClassName="w-full max-w-4xl h-[80vh]"
          >
            <div className="w-full h-full bg-neutral-100 rounded-md overflow-hidden min-h-[50vh]">
              <object
                data={invoiceUrl}
                className="w-full h-full border-0"
                aria-label="Invoice Viewer"
              />
            </div>
          </Modal>
        )}
      </div>
    </Modal>
  )
}
